import axios from 'axios'
import { capitalize } from 'lodash'
import { DateTime, Duration } from 'luxon'
import React, { useEffect, useState } from 'react'
import { Loading } from 'react-admin'
import { useHistory, useParams } from 'react-router'
import { Accordion } from 'shared/components/Accordion/Accordion'
import { Card } from 'shared/components/Base/Card'
import { Divider } from 'shared/components/Base/Divider'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Box } from 'shared/components/Box/Box'
import { Button } from 'shared/components/Button/Button'
import { Text } from 'shared/components/Text/Text'
import { getFormattedDuration, getTimeDifference } from 'shared/helpers/formatters'
import { error, errorD1, lightGray1, lightgreye3, successD1, white } from 'shared/theme/colors'
import { styled } from 'styled-components'

import { StatusBadge } from 'components/Common/StatusBadge.styles'
import { getFormattedDate } from 'helpers/utils'
import { Job, LoadStop } from 'models/jobs'

async function getHosPlan(id: string) {
  try {
    const response = await axios.get(`/api/v1/console/schedules/${id}/hos-plan/`)
    return response.data
  } catch (error: any) {
    console.error(`${error.message}. ${error.response?.data}`)
    throw error
  }
}

async function getSchedule(id: string) {
  try {
    const response = await axios.get(`/api/v1/console/schedules/${id}/`)
    return response.data
  } catch (error: any) {
    console.error(`${error.message}. ${error.response?.data}`)
    throw error
  }
}

interface ScheduleEvent {
  date_start: string
  date_end: string
  load_stop: LoadStop
  hos_plan_events: HOSPlanEvent[]
  drive_date_end: string
  id: string
  job: Job
}

interface HOSPlanEvent {
  duration: number
  state: string
  endTime: string
}

// Create a JS Date from a timestamp (ISO format) and a timezone
const getDate = (date: any, timezone: any) => {
  var formattedDate = DateTime.fromISO(date).setZone(timezone)
  return formattedDate.toLocal().toJSDate()
}

function areTwoDatesSame(date1: Date, date2: Date) {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  )
}

function calculateHosEventEndTimeAndUpdateScheduleEvent(
  hosPlanEvents: HOSPlanEvent[],
  hosPlanIndex: number,
  currentTime: Date,
  scheduleEvent: ScheduleEvent
) {
  let hosEvent = hosPlanEvents[hosPlanIndex]
  let duration = Duration.fromObject({ hours: hosEvent.duration })
  let hosEventEndTime = DateTime.fromJSDate(currentTime).plus(duration)
  hosEvent.endTime = hosEventEndTime.toISO() as string
  scheduleEvent.hos_plan_events.push(hosEvent)
  currentTime = hosEventEndTime.toJSDate()
  return currentTime
}

function mergeScheduleAndHOSPlan(
  scheduleEvents: ScheduleEvent[],
  hosPlanEvents: HOSPlanEvent[]
): ScheduleEvent[] {
  let currentTime

  let hosEventIndex = 0
  for (let i = 0; i < scheduleEvents.length; i++) {
    let event = scheduleEvents[i]
    currentTime = getDate(event.date_start, event.load_stop.location_timezone)
    if (event.hos_plan_events.length > 0) {
      currentTime = getDate(
        event.hos_plan_events[event.hos_plan_events.length - 1].endTime,
        event.load_stop.location_timezone
      )
    }
    let eventEndTime = getDate(event.date_end, event.load_stop.location_timezone)

    while (hosEventIndex < hosPlanEvents.length && currentTime < eventEndTime) {
      currentTime = calculateHosEventEndTimeAndUpdateScheduleEvent(
        hosPlanEvents,
        hosEventIndex,
        currentTime,
        event
      )
      if (currentTime > eventEndTime && i < scheduleEvents.length - 1) {
        let lastEvent = event.hos_plan_events.pop()
        if (lastEvent) {
          let next_event = scheduleEvents[i + 1]
          next_event.hos_plan_events.push(lastEvent)
        }
      }
      hosEventIndex++
    }
  }

  // If there are remaining HOS plan events, add them to the last event
  if (hosEventIndex < hosPlanEvents.length && currentTime) {
    for (let i = hosEventIndex; i < hosPlanEvents.length; i++) {
      currentTime = calculateHosEventEndTimeAndUpdateScheduleEvent(
        hosPlanEvents,
        i,
        currentTime,
        scheduleEvents[scheduleEvents.length - 1]
      )
    }
  }

  // If the last event is AT_FACILITY, move it to the next event
  for (let i = 0; i < scheduleEvents.length; i++) {
    let event = scheduleEvents[i]
    let hosEvents = event.hos_plan_events
    let lastPlanEvent = hosEvents[hosEvents.length - 1]
    if (lastPlanEvent.state.split('.')[1] === 'AT_FACILITY' && i < scheduleEvents.length - 1) {
      let lastEvent = hosEvents.pop()
      if (lastEvent) {
        let next_event = scheduleEvents[i + 1]
        // insert last event as first event of next event
        next_event.hos_plan_events.unshift(lastEvent)
      }
    }
  }
  return scheduleEvents
}

const StyledScheduleEventGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
  margin-top: 1rem;
`

const StyledScheduleEventTitleRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  align-items: flex-start;
`

const ScheduleEventSection = ({
  event,
  eventIndex,
  jobId,
}: {
  event: ScheduleEvent
  eventIndex: number
  jobId: string
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const isLate =
    getDate(event.load_stop.date_end, event.load_stop.location_timezone) <
    getDate(event.drive_date_end, event.load_stop.location_timezone)
  const timeDifference = getTimeDifference(event.load_stop.date_end, event.drive_date_end)
  const arrivalETAStatus = isLate ? `Late by ${timeDifference}` : 'On time'

  const StopText = () => {
    if (event.job.id === jobId) {
      return <Text variant="label2">Stop {event.load_stop.sequence_number}</Text>
    } else if (eventIndex === 0) {
      return (
        <Text variant="label2">
          <a href={`/jobs/${event.job.id}/show`} target="_blank" rel="noreferrer">
            Prior job
          </a>{' '}
          delivery
        </Text>
      )
    } else {
      return (
        <Text variant="label2">
          <a href={`/jobs/${event.job.id}/show`}>Next job</a> PU
        </Text>
      )
    }
  }

  function getFormattedApptTime(): string {
    const start = DateTime.fromISO(event.load_stop.date_start, { zone: event.load_stop.location_timezone })
    const end = DateTime.fromISO(event.load_stop.date_end, { zone: event.load_stop.location_timezone })

    if (start.toMillis() === end.toMillis()) {
      // If dates are equivalent, show only one value without a hyphen
      return start.toFormat('MMMM d, HH:mm ZZZZ')
    } else if (start.toFormat('yyyy-MM-dd') === end.toFormat('yyyy-MM-dd')) {
      // Same day, different times
      const datePart = start.toFormat('MMMM d')
      const startTime = start.toFormat('HH:mm')
      const endTime = end.toFormat('HH:mm')
      const timeZone = start.toFormat('ZZZZ')
      return `${datePart}, ${startTime}-${endTime} ${timeZone}`
    } else {
      // Different days
      const startDatePart = start.toFormat('MMMM d, HH:mm')
      const endDatePart = end.toFormat('MMMM d, HH:mm')
      const timeZone = start.toFormat('ZZZZ') // Assuming timezone is the same for both dates
      return `${startDatePart} - ${endDatePart} ${timeZone}`
    }
  }

  const StopLocationRow = () => (
    <>
      {StopText()}
      <Text variant="paragraph1" fontWeight={600}>
        {event.load_stop.location_city}, {event.load_stop.location_state}
      </Text>
      <StatusBadge $color={isLate ? errorD1 : successD1} $backgroundColor={isLate ? error : white}>
        {arrivalETAStatus}
      </StatusBadge>
      <FlexBox alignItems="center" gap="8px">
        <Text variant="label2">{getFormattedApptTime()}</Text>
        <Text variant="label4" fontWeight={600} fontColor={lightGray1}>
          APPT
        </Text>
      </FlexBox>
    </>
  )
  return (
    <Accordion
      isExpanded={isExpanded}
      onClick={(isExpanded) => setIsExpanded(isExpanded)}
      align="flex-start"
      details={
        <FlexBox isColumn>
          <StyledScheduleEventGrid>
            {event.hos_plan_events?.map((hosEvent: HOSPlanEvent, index: number) => {
              let includeDateInFormatting = true
              if (index >= 1) {
                let previousEndDate = getDate(
                  event.hos_plan_events[index - 1].endTime,
                  event.load_stop.location_timezone
                )
                let currentEndDate = getDate(hosEvent.endTime, event.load_stop.location_timezone)
                if (areTwoDatesSame(previousEndDate, currentEndDate)) {
                  includeDateInFormatting = false
                }
              }
              let endTime = getFormattedDate(
                hosEvent.endTime,
                event.load_stop.location_timezone,
                includeDateInFormatting
              )
              if (index === event.hos_plan_events.length - 1) {
                endTime = `${endTime} ETA`
              }
              return (
                <React.Fragment key={index}>
                  <Text variant="paragraph1">
                    {capitalize(hosEvent.state.split('.')[1].replace('_', ' '))}
                  </Text>
                  <Text variant="paragraph1">{getFormattedDuration(hosEvent.duration)}</Text>
                  <Text variant="paragraph1">{endTime}</Text>
                </React.Fragment>
              )
            })}
          </StyledScheduleEventGrid>
          <StyledScheduleEventTitleRow>
            <StopLocationRow />
          </StyledScheduleEventTitleRow>
        </FlexBox>
      }
      useUnfoldIcon
    >
      {(isExpanded) =>
        isExpanded ? (
          <Box />
        ) : (
          <StyledScheduleEventTitleRow>
            <StopLocationRow />
          </StyledScheduleEventTitleRow>
        )
      }
    </Accordion>
  )
}

function ScheduleInspect(props: any) {
  const title = props.history?.location?.state?.title
  const [hosPlan, setHosPlan] = useState<any>(null)
  const [schedule, setSchedule] = useState<any>(null)
  const { jobId, scheduleId } = useParams<{ jobId: string; scheduleId: string }>()
  const [error, setError] = useState<boolean>(false)
  const history = useHistory()

  useEffect(() => {
    getHosPlan(scheduleId)
      .then((response) => {
        setHosPlan(response)
      })
      .catch(() => {
        setError(true)
      })
  }, [scheduleId])

  useEffect(() => {
    getSchedule(scheduleId)
      .then((response) => {
        setSchedule(response)
      })
      .catch(() => {
        setError(true)
      })
  }, [scheduleId])

  if (error) {
    return (
      <Text variant="label2">
        Something went wrong. Please try refreshing the page. If the issue persists, please file a bug in
        #tech-support.
      </Text>
    )
  }

  if (!hosPlan || !schedule) {
    return <Loading />
  }

  let events = schedule?.events.map((event: ScheduleEvent) => {
    return {
      ...event,
      hos_plan_events: [],
    }
  })

  let hosPlanEvents = hosPlan?.events.map((event: HOSPlanEvent) => {
    // The backend only sets the state to ON_DUTY during the event dwell time, and we want to serialize it as AT_FACILITY to the user
    if (event.state.split('.')[1] === 'ON_DUTY') {
      event.state = 'State.AT_FACILITY'
    }
    return event
  })

  // Merge the HOS plan events with the schedule events
  const mergedEvents = mergeScheduleAndHOSPlan(events, hosPlanEvents)

  return (
    <Card align="left">
      <Button
        onClick={() => {
          history.goBack()
        }}
        variant="text"
      >
        ← Go back
      </Button>
      <Box mb="28px" />
      <Text variant="header2">{title}</Text>
      <Box mb="40px" />
      <FlexBox isColumn gap="24px" width="60%">
        {mergedEvents.map((event: ScheduleEvent, index: number) => (
          <React.Fragment key={index}>
            <ScheduleEventSection event={event} eventIndex={index} jobId={jobId} />
            {index < mergedEvents.length - 1 && <Divider color={lightgreye3} />}
          </React.Fragment>
        ))}
      </FlexBox>
      <Box mb="40px" />
      <Button onClick={() => history.push(`/users/${schedule?.driver.id}/show#jobs`)} variant="secondary">
        VIEW DRIVER'S JOBS
      </Button>
    </Card>
  )
}

export default ScheduleInspect
